// Estilos base del mapa.
// Define colores y propiedades visuales compartidas para que cualquier ajuste
// de la UI cartográfica salga de un solo lugar.

import { colors } from '../../styles/theme';
import { createCadMasterplanStyle } from './cadMasterplanStyleUtils';

function parseThemeRgb(hex) {
  const raw = String(hex || '').replace('#', '');
  const body = raw.length === 3
    ? raw.split('').map((ch) => `${ch}${ch}`).join('')
    : raw.slice(0, 6);
  const n = parseInt(body, 16);
  if (!Number.isFinite(n)) return { r: 0, g: 0, b: 0 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Mezcla tokens de `theme.js`. No introduce hex sueltos. */
function mixThemeHex(fromHex, toHex, amount) {
  const from = parseThemeRgb(fromHex);
  const to = parseThemeRgb(toHex);
  const t = Math.min(1, Math.max(0, Number(amount) || 0));
  const channel = (a, b) => Math.round(a + (b - a) * t).toString(16).padStart(2, '0');
  return `#${channel(from.r, to.r)}${channel(from.g, to.g)}${channel(from.b, to.b)}`;
}

/** Lote disponible: pasto inmobiliario, más cerca del success que del lavado. */
const CAD_LOT_GRASS_WASH = mixThemeHex(colors.backgroundLight, colors.buildingNeutral, 0.28);
const CAD_LOT_GRASS_FILL = mixThemeHex(colors.success, CAD_LOT_GRASS_WASH, 0.24);

/**
 * Cinco pastos casi iguales alrededor de `CAD_LOT_GRASS_FILL`. Un loteo de 200
 * lotes con un verde único se lee como una pared; el tono sale de `tone_seed`
 * (hash del id), así que es estable entre renders y no cuesta geometría.
 */
const CAD_LOT_GRASS_TONES = [0.18, 0.22, 0.24, 0.30, 0.34].map(
  (amount) => mixThemeHex(colors.success, CAD_LOT_GRASS_WASH, amount),
);

/** Zoom mínimo para lotes: API getlotbypoint y capas PMTiles de parcelas. */
export const MAP_MIN_LOT_ZOOM = 10; // ZOOM TILES PARA VER PARCELAS

/** Zoom mínimo para centroides filtrados (capa puntos, vista país/provincia). */
export const PARCEL_FILTER_POINTS_MIN_ZOOM = 0;

/** Zoom tope (exclusivo) para centroides filtrados; polígonos desde aquí. */
export const PARCEL_FILTER_POINTS_MAX_ZOOM = 12;

/**
 * Zoom por encima del cual cerrar el breadcrumb administrativo no resetea
 * la cámara. En un barrio el chip se quita porque molesta visualmente;
 * no debe volar a la vista país.
 */
export const ADMIN_CLEAR_KEEP_CAMERA_ABOVE_ZOOM = PARCEL_FILTER_POINTS_MAX_ZOOM;

/**
 * Zoom mínimo para correr `POST /filterofficialunits`. Más lejos el bbox es una
 * ciudad: los puntos de parcela son una muestra y no hay forma de pintar todas
 * las UF. Mismo corte que el paso de puntos → polígonos.
 */
export const OFFICIAL_UNIT_FILTER_MIN_ZOOM = PARCEL_FILTER_POINTS_MAX_ZOOM;

// Apariencia de capas estructurales del mapa, como el fondo y los límites
// administrativos visibles sobre la cartografía base.
export const MAP_BASE_STYLE = {
  backgroundColor: '#ffffff',
  provincesBorderColor: '#ffffff',
  provincesBorderColorStreet: '#000000',
  provincesBorderWidth: 1.5,
  provincesBorderDasharray: [2, 2],
  // Hover de provincia (relleno + borde contrastado sobre satélite)
  // provincesHoverFillColor: 'rgba(72,146,145,0.18)',
  provincesHoverFillOpacity: 1,
  provincesHoverColor: '#489291',
  // Estilo administrativo unificado (departamentos/municipios/pedanias).
  administrativeHoverColor: '#489291',
  administrativeHoverFillColor: 'rgba(72,146,145,0.18)',
  administrativeHoverLineWidth: 2.6,
  administrativeSelectedColor: '#489291',
  administrativeSelectedLineWidth: 3.2,
  administrativeHoverFillFadeStartZoom: 11.5,
  administrativeHoverFillFadeEndZoom: 12.8,
  administrativeBordersColorLightMap: '#1f2937',
  administrativeBordersColorDarkMap: '#ffffff',
  provincesHoverWidth: 4.5,
  /** tope de zoom al encuadrar una provincia (fitBounds) al hacer click */
  provinceFitMaxZoom: 6.0,
  /** tope de zoom específico para CABA al seleccionar provincia */
  cabaProvinceFitMaxZoom: 12.0,
  /** tope de zoom al encuadrar un departamento seleccionado */
  departmentFitMaxZoom: 14.5,
  /** tope de zoom específico para departamentos de CABA (más pequeños) */
  cabaDepartmentFitMaxZoom: 15.5,
  /** tope de zoom al encuadrar un municipio seleccionado */
  municipalityFitMaxZoom: 15.5,
  /** tope de zoom específico para municipios de CABA (si aplica) */
  cabaMunicipalityFitMaxZoom: 16.0,
  /** padding específico para encuadres de CABA */
  cabaProvinceFitPadding: 24,
  cabaDepartmentFitPadding: 12,
  cabaMunicipalityFitPadding: 10,
  /** compensación superior para header fijo al hacer fitBounds administrativo */
  administrativeFitTopInset: 72,
  /** estilo de límites administrativos de barrios */
  // Satélite/base oscura: borde principal acromático.
  neighborhoodBorderColor: '#ffffff',
  // Calle/base clara: gris medio, legible sin endurecer demasiado.
  neighborhoodBorderColorStreet: '#6B6B6B',
  neighborhoodBorderOpacity: 0.92,
  neighborhoodBorderWidthByZoom: {
    min: 1,
    max: 0.5,
    zoomSpan: 4,
  },
};

// Estilo de las parcelas provenientes de PMTiles.
// Estas capas representan el entramado general de lotes visible en el mapa.
export const PARCEL_LAYER_STYLE = {
  fillColor: '#0080ff',
  fillOpacity: 0,
  lineColor: '#BDBDBD',
  // // lineColor: '#9E9E9E', Se dejan opciones para posibles futuros cambios
  // lineColor: '#757575',
  // lineColor: '#D0D0D0',
  lineOpacity: 1,
  lineWidth: 0.5, // revisar WIDTH parcelas
};

// Estilo del lote seleccionado cuando se dibuja dinámicamente luego de una
// búsqueda o interacción puntual del usuario.
export const SELECTED_LOT_STYLE = {
  fillColor: colors.primary,
  fillOpacity: 0.3,
  outlineColor: colors.primary,
  borderColor: colors.primary,
  // Más grueso que el hover (4) para que el lote elegido gane sobre el cursor.
  borderWidth: 4.5,
  borderOpacity: 0.8,
};

// Estilo de resaltado (hover) sobre parcelas al mover el cursor.
export const HOVER_LOT_STYLE = {
  // fillColor: '#489291',
  // fillOpacity: 0.05,
  // borderColor: '#489291',
  // borderWidth: 2,
  fillColor: '#7FE3D2',
  fillOpacity: 0,
  borderColor: '#489291',
  borderWidth: 4,
  cursor: 'pointer',
};

// Colores asignados a cada valor de filtro para resaltar visualmente las
// parcelas que coinciden con los criterios seleccionados en el FilterPanel.
export const FILTER_CATEGORY_COLORS = {
  type: {
    URBANO: '#38bdf8',
    RURAL:  '#86efac',
  },
  state: {
    'EDIFICADO PH':   '#fbbf24',
    'EDIFICADO NoPH': '#fb923c',
    'BALDIO':         '#a78bfa',
  },
};

// Propiedades de estilos aplicadas a parcelas que coinciden con los filtros
// activos. highlightColor es el color primario único para todas las coincidencias.
export const FILTER_HIGHLIGHT_STYLE = {
  highlightColor: '#489291',
  fillOpacity: 0,
  lineWidth: 2,
};

/** Resaltado de polígonos de lote: registros y listas. */
const LOT_HIGHLIGHT_COLOR = colors.primary;

/**
 * Pines de lote a zoom lejano, en color primario. Compartido por los pines
 * GeoJSON (`useListPinsLayer`: listas, registros, alcance de filtros) y los
 * centroides del tile (`parcelas_puntos`), para que se vean igual en ambos casos:
 * círculo sólido con borde, sin halo. El radio es el de los pines de lista.
 */
export const LOT_PIN_STYLE = {
  color: colors.primary,
  strokeColor: MAP_BASE_STYLE.backgroundColor,
  strokeWidth: 1.5,
  circleOpacity: 0.9,
  circleRadius: ['interpolate', ['linear'], ['zoom'], 0, 5, 6, 3, 12.5, 5],
};

/**
 * Paleta de pines de puntos de interés. Un color por kind (museo y
 * supermercado comparten naranja a pedido de producto).
 */
export const REFERENCE_PLACES_KIND_COLORS = {
  school: '#3B82F6',
  airport: '#38BDF8',
  hospital: '#EF5350',
  church: '#C9A227',
  national_park: '#388E3C',
  park: '#66BB6A',
  police: '#6366F1',
  prison: '#667085',
  library: '#F59E0B',
  museum: '#F97316',
  monument: '#B7794C',
  cultural_org: '#8B5CF6',
  sports: '#2563EB',
  gym: '#7C3AED',
  supermarket: '#F97316',
  mall: '#EC4899',
  landfill: '#8D6E63',
  default: colors.textSecondary,
};

export const REFERENCE_PLACES_PIN_STYLE = {
  strokeColor: MAP_BASE_STYLE.backgroundColor,
  iconPixelSize: 64,
  iconPixelRatio: 2,
  iconSize: ['interpolate', ['linear'], ['zoom'], 10, 0.72, 14, 1, 17, 1.12],
};

/**
 * Pin de lista CAD en el mapa país. Misma familia visual que puntos de
 * interés (badge + ícono), color distinto al pin teal de lote.
 */
export const CAD_LIST_PIN_STYLE = {
  fillColor: colors.infoBlue,
  strokeColor: REFERENCE_PLACES_PIN_STYLE.strokeColor,
  iconPixelSize: REFERENCE_PLACES_PIN_STYLE.iconPixelSize,
  iconPixelRatio: REFERENCE_PLACES_PIN_STYLE.iconPixelRatio,
  iconSize: REFERENCE_PLACES_PIN_STYLE.iconSize,
};

export const CAD_LIST_PIN_IMAGE_ID = 'cad-list-pin';
/** Capa symbol del pin de lista CAD en el mapa país. */
export const CAD_LIST_PINS_LAYER_ID = 'cad-list-pins-dots';
export const CAD_LIST_PINS_SOURCE_ID = 'cad-list-pins';

/**
 * Pines propios de una lista CAD (`cad_project_pins`). No son
 * `reference_places` ni el pin de la lista en el mapa país.
 * Colores = tokens de `theme.js` (sin hex nuevos).
 */
export const CAD_PROJECT_PIN_KIND_COLORS = {
  entrance: colors.infoBlue,
  security: colors.primary,
  green: colors.naturalGreen,
  amenity: colors.warning,
  custom: colors.primaryLight,
  default: colors.textSecondary,
};

export const CAD_PROJECT_PIN_STYLE = {
  strokeColor: REFERENCE_PLACES_PIN_STYLE.strokeColor,
  iconPixelSize: REFERENCE_PLACES_PIN_STYLE.iconPixelSize,
  iconPixelRatio: REFERENCE_PLACES_PIN_STYLE.iconPixelRatio,
  /**
   * Corte en zoom país. El radio usa `interpolate` con `case` en cada
   * stop (feature-state + zoom); case envolviendo dos interpolates es
   * inválido en MapLibre 3 y la capa no se crea.
   */
  minZoom: 11,
  /** [zoom, radiusNormal, radiusHighlight] */
  circleRadiusStops: [
    [11, 3, 5],
    [13, 5, 7],
    [14.5, 7, 10],
    [17, 8, 11],
  ],
  circleStrokeWidth: ['interpolate', ['linear'], ['zoom'], 11, 1, 14.5, 2],
  circleOpacity: [
    'interpolate', ['linear'], ['zoom'],
    11.25, 0,
    13, 0.95,
  ],
  iconSize: ['interpolate', ['linear'], ['zoom'], 11, 0.4, 13, 0.65, 14.5, 0.9, 17, 1.05],
};

/**
 * Radio zoom + highlight. Forma válida en MapLibre 3 (`composite`).
 * @returns {unknown[]}
 */
export function buildCadProjectPinCircleRadiusPaint() {
  const expr = ['interpolate', ['linear'], ['zoom']];
  CAD_PROJECT_PIN_STYLE.circleRadiusStops.forEach(([zoom, normal, highlight]) => {
    expr.push(
      zoom,
      ['case', ['boolean', ['feature-state', 'highlight'], false], highlight, normal],
    );
  });
  return expr;
}

export const CAD_PROJECT_PIN_IMAGE_PREFIX = 'cad-project-pin-';

/** Centroides de parcelas filtradas a zoom lejano (`parcelas_puntos`). */
export const PARCEL_POINTS_LAYER_STYLE = {
  circleColor: LOT_PIN_STYLE.color,
  circleOpacity: LOT_PIN_STYLE.circleOpacity,
  circleStrokeWidth: LOT_PIN_STYLE.strokeWidth,
  circleStrokeColor: LOT_PIN_STYLE.strokeColor,
  circleRadius: LOT_PIN_STYLE.circleRadius,
};

// Estilo para resaltar registros cuando el panel de Registros está abierto,
// y los lotes que ya están en una lista común. El relleno tiene que leerse
// sobre satélite; 0.1 se percibía como solo borde.
export const MY_LOTS_HIGHLIGHT_STYLE = {
  fillColor: LOT_HIGHLIGHT_COLOR,
  fillOpacity: 0.4,
  fillOpacityHover: 0.62,
  lineColor: LOT_HIGHLIGHT_COLOR,
  lineWidth: 3.5,
};

// Lotes stagiados al agregar a una lista (aún no confirmados):
// mismo color que el highlight de lista, pero solo contorno (sin relleno).
export const LIST_PENDING_ADD_HIGHLIGHT_STYLE = {
  fillColor: LOT_HIGHLIGHT_COLOR,
  fillOpacity: 0,
  lineColor: LOT_HIGHLIGHT_COLOR,
  lineWidth: 3.5,
};

/**
 * @param {string|number} tilesetId
 * @returns {string}
 */
export function parcelSelectedGlowLayerId(tilesetId) {
  return `self-hosted-vector-selected-glow-${tilesetId}`;
}

/**
 * Halo + núcleo del lote seleccionado (listas catastrales y CAD).
 * El glow va en una capa `line` con blur; el núcleo en paint o capa nítida.
 */
export const SELECTED_LOT_GLOW_STYLE = {
  lineColor: colors.backgroundLight,
  lineWidth: 3.6,
  glowColor: colors.primaryLight,
  glowWidth: 14,
  glowBlur: 6,
  glowOpacity: 0.9,
};

/** Filtro MapLibre que no matchea ningún id (capa de selección apagada). */
export const EMPTY_SELECTED_LOT_FILTER = ['==', ['get', 'id'], ''];

/**
 * @param {string|number|null|undefined} lotId
 * @returns {object}
 */
export function buildSelectedLotIdFilter(lotId) {
  if (lotId == null || lotId === '') return EMPTY_SELECTED_LOT_FILTER;
  return ['==', ['get', 'id'], String(lotId)];
}

// Lote seleccionado dentro de una lista activa (Mis listas / compartidos): más marcado que el resto.
export const LIST_SELECTED_LOT_HIGHLIGHT_STYLE = {
  fillColor: SELECTED_LOT_STYLE.fillColor,
  fillOpacity: SELECTED_LOT_STYLE.fillOpacity,
  lineColor: SELECTED_LOT_GLOW_STYLE.lineColor,
  lineWidth: SELECTED_LOT_GLOW_STYLE.lineWidth,
};

/**
 * Detalle de lote desde una lista (compartida o Mis listas): solo contorno del lote
 * (sin relleno verde) para que el modelo 3D siga legible debajo.
 * Núcleo blanco; el halo luminoso lo pinta la capa PMTiles `*-selected-glow-*`.
 */
export const SHARED_LIST_SELECTED_LOT_HIGHLIGHT_STYLE = {
  fillColor: SELECTED_LOT_GLOW_STYLE.lineColor,
  fillOpacity: 0,
  lineColor: SELECTED_LOT_GLOW_STYLE.lineColor,
  lineWidth: SELECTED_LOT_GLOW_STYLE.lineWidth,
};

// Colores de estado para lotes vinculados o no a la compañía seleccionada.
// Se usan al actualizar visualmente el polígono luego de editar o sincronizar
// su información.
export const LOT_STATUS_COLORS = {
  linkedToSelectedCompany: '#ff0000',
  notLinkedToSelectedCompany: '#00ff00',
};

// Estilos para la capa de edificios 3D (footprint 2D + fill-extrusion).
export const BUILDINGS_LAYER_STYLE = {
  // Relleno 2D (fill)
  fillColor: colors.primary,
  fillOpacity: 0.8,
  // Share CAD (Terrio y branded): crema cálido, no el acento
  neutralFillColor: colors.buildingNeutral,
  // Contorno 2D (footprint)
  outlineColor: '#ffffff',
  outlineWidth: 2,
  // Extrusión 3D (fill-extrusion) — estado normal
  extrusionColor: colors.primary,
  neutralExtrusionColor: colors.buildingNeutral,
  extrusionOpacity: 1,
  // Extrusión 3D — cuando el edificio corresponde al lote seleccionado en el DockPanel
  extrusionSelectedColor: colors.primary,
  extrusionSelectedOpacity: 1,
  extrusionDefaultHeight: 3,    // metros usados cuando el dato 'height' es nulo
  extrusionMinZoom: 14,          // zoom mínimo para activar la extrusión
};

/** Zoom mínimo del mapa para que el usuario vea cada capa de referencia en pantalla. */
export const REFERENCE_LAYERS_MIN_ZOOM = {
  pobrezaCronicaArgentina: 4,
  zonasInundables: 5,
  /** Zonificación urbana — Provincia de Córdoba (IDECOR 406) */
  zonificacionUrbana: 6,
};

// Estilos para la capa de zonificación urbana (Córdoba provincial).
// El color de relleno viene embebido en el tile (fill_color) — sin paleta hardcodeada.
export const ZONIFICACION_URBANA_LAYER_STYLE = {
  fillOpacity: 0.32,
  fillFallbackColor: '#CCCCCC',
  outlineColor: '#333333',
  strokeWidth: 0.8,
  strokeOpacity: 0.5,
};

// Estilos para la capa Pobreza Crónica Argentina (Censo 2010).
// El color de relleno viene embebido en el tile (fill_color).
export const POBREZA_CRONICA_ARGENTINA_LAYER_STYLE = {
  fillOpacity: 0.34,
  fillFallbackColor: '#BDBDBD',
  outlineColor: '#333333',
};

/** Fondo del plano CAD (`canvas` / `image`). No aplica en modo mapa. */
export const CAD_PLAN_BACKGROUND = {
  dark: colors.backgroundDark,
  light: colors.backgroundLight,
};

export function getCadPlanBackgroundColor(mode) {
  return mode === 'light' ? CAD_PLAN_BACKGROUND.light : CAD_PLAN_BACKGROUND.dark;
}

/**
 * Overlay de parcelas CAD del showroom (custom_parcels). No son lots de catastro.
 * Colores semánticos de `styles/theme.js` — stock_status del contrato CAD.
 */
export const CAD_PARCEL_STOCK_STYLE = {
  fillOpacity: 1,
  fillOpacityHover: 1,
  fillOpacitySelected: 1,
  /**
   * Antes era 0 para “ver el satélite” bajo el filtro. Con masterplan (calle /
   * cordón / árboles debajo del fill) eso abre una ventana al asfalto y a
   * límites que parecen calles internas. Opaco + `stockFillMuted` tapa el contexto.
   */
  fillOpacityMuted: 1,
  /** Velo de lote fuera de filtro / stock oculto (no es color de estado). */
  stockFillMuted: mixThemeHex(colors.buildingNeutral, colors.backgroundDark, 0.12),
  /** Contorno en reposo: no compite con el relleno de estado. El teal es solo hover. */
  lineColor: colors.backgroundLight,
  lineColorHover: colors.primary,
  lineColorMuted: colors.textSecondary,
  lineOpacity: 1,
  lineOpacityHover: 1,
  lineOpacityMuted: 0.35,
  lineWidth: 1.2,
  lineWidthHover: 3.4,
  // Vista compartida: línea blanca + relleno de stock más suave (se lee
  // vendido/reservado sin tapar satélite ni plano base).
  shareFillOpacity: 1,
  shareFillOpacityHover: 1,
  shareFillOpacitySelected: 1,
  shareLineColor: colors.backgroundLight,
  shareLineOpacity: 1,
  shareLineOpacityMuted: 0.35,
  shareLineWidth: 1.6,
  shareLineWidthHover: 3.4,
  // Alinear la ilustración: solo el contorno del loteo. El render se atenúa.
  alignFillOpacity: 0,
  alignLineColor: colors.primaryLight,
  alignLineOpacity: 1,
  alignLineWidth: 2.2,
  alignRasterOpacity: 0.45,
  // Lote abierto en la ficha: más gordo de lejos (el 3.6px blanco se
  // perdía entre el resto). Cerca gana al hover (3.4) sin tapar el lote.
  lineColorSelected: SELECTED_LOT_GLOW_STYLE.lineColor,
  lineWidthSelected: [
    'interpolate', ['linear'], ['zoom'],
    15, 7,
    18, 5,
    20, 4.5,
  ],
  lineColorSelectedGlow: SELECTED_LOT_GLOW_STYLE.glowColor,
  lineWidthSelectedGlow: [
    'interpolate', ['linear'], ['zoom'],
    15, 20,
    18, 16,
    20, 14,
  ],
  lineBlurSelectedGlow: 3.5,
  lineOpacitySelectedGlow: 1,
  stockFill: {
    available: CAD_LOT_GRASS_FILL,
    reserved: colors.warning,
    sold: colors.error,
    offer: colors.infoBlue,
    unavailable: colors.textSecondary,
  },
  /** Solo el relleno del mapa. Leyenda, chip y tooltip siguen con `stockFill.available`. */
  grassTones: CAD_LOT_GRASS_TONES,
  /** Chip sobre el lote: entra en este zoom. Fade corto (`labelFadeZoom`), no un interpolado largo. */
  labelMinZoom: 17.5,
  labelFadeZoom: 0.12,
  chipFill: colors.backgroundLight,
  chipText: colors.textDark,
  chipBorder: colors.inputBorder,
};

/**
 * Masterplan sobre geometría CAD exacta (`cad_kind`).
 * Look en `cadMasterplanStyleUtils.js` (`createCadMasterplanStyle`).
 */
export {
  createCadMasterplanStyle,
  metric,
  metersPerPixel,
  CAD_MASTERPLAN_MATERIALS,
  CAD_MASTERPLAN_SUN,
} from './cadMasterplanStyleUtils';

export const CAD_MASTERPLAN_STYLE = createCadMasterplanStyle();

// Estilos para la capa de zonas inundables.
// Colores indexados por `incidencia` (1=Leve, 2=Moderada, 3=Crítica).
export const ZONAS_INUNDABLES_LAYER_STYLE = {
  fillOpacity: 0.35,
  outlineColor: '#333333',
  outlineWidth: 0.5,
  outlineOpacity: 0.4,
  incidenciaColors: {
    1: '#4daf4a', // Leve
    2: '#ffd92f', // Moderada
    3: '#e41a1c', // Crítica
    fallback: '#9e9e9e',
  },
};

const mapTheme = {
  MAP_BASE_STYLE,
  PARCEL_LAYER_STYLE,
  SELECTED_LOT_STYLE,
  SELECTED_LOT_GLOW_STYLE,
  HOVER_LOT_STYLE,
  FILTER_CATEGORY_COLORS,
  FILTER_HIGHLIGHT_STYLE,
  LOT_PIN_STYLE,
  REFERENCE_PLACES_PIN_STYLE,
  CAD_LIST_PIN_STYLE,
  CAD_PROJECT_PIN_KIND_COLORS,
  CAD_PROJECT_PIN_STYLE,
  CAD_PROJECT_PIN_IMAGE_PREFIX,
  REFERENCE_PLACES_KIND_COLORS,
  PARCEL_POINTS_LAYER_STYLE,
  MY_LOTS_HIGHLIGHT_STYLE,
  LOT_STATUS_COLORS,
  BUILDINGS_LAYER_STYLE,
  ZONAS_INUNDABLES_LAYER_STYLE,
  ZONIFICACION_URBANA_LAYER_STYLE,
  POBREZA_CRONICA_ARGENTINA_LAYER_STYLE,
  CAD_PLAN_BACKGROUND,
  CAD_PARCEL_STOCK_STYLE,
  CAD_MASTERPLAN_STYLE,
};

export default mapTheme;
